import { panelThemeClasses } from "../../theme/themeClasses";
import type { Tone } from "../../components/ui";

/** @deprecated Use the semantic Tailwind tokens directly (bg-surface, bg-card, text-content, border-line…). */
export const buildThemeClasses = () => panelThemeClasses();

// Lifecycle vocabularies (kept in sync with the backend maintenance module).
// The visible labels live in i18n (`mantenimiento.types.*`, `.statuses.*`,
// `.modes.*`, `.transitions.*`): see hooks/useMxLabels.
export const MAINTENANCE_TYPES = [
  "programado",
  "solicitado",
  "sugerido",
] as const;

export const MAINTENANCE_STATUSES = [
  "pendiente",
  "agendado",
  "en_proceso",
  "completado",
  "cancelado",
] as const;

export const WORKSHOP_MODES = ["propio", "externo"] as const;

export const APPOINTMENT_STATUSES = [
  "reservado",
  "confirmado",
  "en_proceso",
  "completado",
  "cancelado",
] as const;

/** Tono de cada estado (skill estados-y-tonos): un estado, un tono. */
export const STATUS_TONES = {
  pendiente: "warning",
  agendado: "info",
  en_proceso: "info",
  completado: "success",
  cancelado: "neutral",
} as const satisfies Record<(typeof MAINTENANCE_STATUSES)[number], Tone>;

/** «Vencido» no es un estado del backend: se deduce y es una alerta. */
export const OVERDUE_TONE: Tone = "error";

export const APPT_STATUS_TONES = {
  reservado: "info",
  confirmado: "success",
  en_proceso: "warning",
  completado: "neutral",
  cancelado: "error",
} as const satisfies Record<(typeof APPOINTMENT_STATUSES)[number], Tone>;

export const MODE_TONES = {
  propio: "info",
  externo: "warning",
} as const satisfies Record<(typeof WORKSHOP_MODES)[number], Tone>;

export const STATUS_ICONS: Record<string, string> = {
  pendiente: "ri-time-line",
  agendado: "ri-calendar-check-line",
  en_proceso: "ri-tools-line",
  completado: "ri-checkbox-circle-line",
  cancelado: "ri-close-circle-line",
};

export const TYPE_ICONS: Record<string, string> = {
  programado: "ri-calendar-schedule-line",
  solicitado: "ri-hand-heart-line",
  sugerido: "ri-lightbulb-flash-line",
};

// Seguimiento de estados: máquina de estados del ciclo de mantenimiento. Mirror
// of the backend STATUS_TRANSITIONS — drives the quick-action buttons and the
// kanban drop targets. Keep both in sync.
export const STATUS_TRANSITIONS: Record<string, string[]> = {
  pendiente: ["agendado", "en_proceso", "cancelado"],
  agendado: ["en_proceso", "pendiente", "cancelado"],
  en_proceso: ["completado", "cancelado"],
  completado: [],
  cancelado: ["pendiente"],
};

// Icon + color of the quick-transition button that moves a record INTO a
// given status (label: `mantenimiento.transitions.<status>`).
export const STATUS_ACTIONS: Record<string, { icon: string; className: string }> = {
  agendado: { icon: "ri-calendar-check-line", className: "text-info hover:bg-info/10" },
  en_proceso: { icon: "ri-play-circle-line", className: "text-warning hover:bg-warning/10" },
  completado: { icon: "ri-checkbox-circle-line", className: "text-success hover:bg-success/10" },
  cancelado: { icon: "ri-close-circle-line", className: "text-error hover:bg-error/10" },
  pendiente: { icon: "ri-refresh-line", className: "text-content-secondary hover:bg-subtle" },
};

// --- Categorías (para gráficas y filtros) ------------------------------------
// El registro no guarda categoría: se deduce de la descripción del servicio.
export const CATEGORIES = [
  "motor",
  "frenos",
  "llantas",
  "lubricacion",
  "electrico",
  "diagnostico",
  "refrigeracion",
  "suspension",
  "carroceria",
  "otro",
] as const;
export type Category = (typeof CATEGORIES)[number];

const CATEGORY_KEYWORDS: [Category, RegExp][] = [
  ["refrigeracion", /refrigera|térmic|termic|frío|frio/i],
  ["frenos", /fren|balata|disco|cámara|camara/i],
  ["llantas", /llanta|alinea|balance|rotaci/i],
  ["lubricacion", /aceite|lubric|filtro|grasa|quinta rueda/i],
  ["electrico", /alternador|bater|eléctr|electr|marcha|foco/i],
  ["diagnostico", /diagn|escáner|escaner|verificaci|inspecci|check engine/i],
  ["suspension", /amortigu|suspensi|buje|muelle/i],
  ["carroceria", /carrocer|pintura|hojalat|defensa/i],
  ["motor", /motor|inyector|embrague|escape|dpf|enfriamiento|banda|servicio mayor/i],
];

export function categoryOf(description?: string | null): Category {
  const text = description || "";
  for (const [cat, re] of CATEGORY_KEYWORDS) if (re.test(text)) return cat;
  return "otro";
}

export const CATEGORY_ICONS: Record<Category, string> = {
  motor: "ri-settings-5-line",
  frenos: "ri-steering-2-line",
  llantas: "ri-donut-chart-line",
  lubricacion: "ri-drop-line",
  electrico: "ri-flashlight-line",
  diagnostico: "ri-pulse-line",
  refrigeracion: "ri-snowflake-line",
  suspension: "ri-car-line",
  carroceria: "ri-paint-brush-line",
  otro: "ri-tools-line",
};

// Paleta de gráficas: tonos medios que se leen en claro y en oscuro. Las
// series siguen el orden de CATEGORIES / estados.
export const CHART_COLORS = [
  "#3b82f6",
  "#f59e0b",
  "#10b981",
  "#8b5cf6",
  "#ef4444",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
  "#f97316",
  "#64748b",
] as const;

export const STATUS_CHART_COLORS: Record<string, string> = {
  pendiente: "#f59e0b",
  agendado: "#3b82f6",
  en_proceso: "#8b5cf6",
  completado: "#10b981",
  cancelado: "#94a3b8",
};

export const MODE_CHART_COLORS = { propio: "#3b82f6", externo: "#f59e0b" } as const;

/** Ejes y rejilla de recharts con los tokens del tema activo. */
export const CHART_AXIS = {
  tick: { fontSize: 11, fill: "rgb(var(--c-content-muted))" },
  grid: "rgb(var(--c-border))",
  tooltip: {
    background: "rgb(var(--c-card))",
    border: "1px solid rgb(var(--c-border))",
    borderRadius: 8,
    fontSize: 12,
    color: "rgb(var(--c-content))",
  },
} as const;
